<template>
	<SlideoutShell :open="open" @close="$emit('close')">
		<SlideoutHeader
			:active-tab="activeTab"
			:show-delete-all="showDeleteAll"
			:show-header-actions="showHeaderActions"
			:show-mark-all-as-read="showMarkAllAsRead"
			:unread-tab-label="unreadTabLabel"
			@close="$emit('close')"
			@delete-all="deleteAll"
			@mark-all-as-read="markAllAsRead"
			@switch-tab="switchTab"
		/>

		<NotificationsList
			:has-more="hasMoreNotifications"
			:is-loading-more="isLoadingList"
			:notifications="notifications"
			:empty-state-message="emptyStateMessage"
			:empty-state-title="$t('notification.empty-state.title')"
			:notification-badge-class="notificationBadgeClass"
			:notification-badge-icon="notificationBadgeIcon"
			:is-touch-device="$isTouchDevice"
			:is-unread="isUnread"
			:notification-fallback-icon="notificationFallbackIcon"
			:notification-image-url="notificationImageUrl"
			:notification-relative-time="notificationRelativeTime"
			:resolve-content="resolveContent"
			@delete-notification="deleteNotification"
			@load-more="loadMoreNotifications"
			@mark-read="markNotificationAsRead"
			@notification-click="handleNotificationClick"
		/>
	</SlideoutShell>
</template>

<script>
import { SlideoutShell, SlideoutHeader, NotificationsList } from './_internals'
import {
	getNotificationAppearance,
	getNotificationMedia,
	resolveNotificationContent
} from '~/util/notifications'

export default {
	name: 'NotificationsSlideout',
	components: {
		SlideoutShell,
		SlideoutHeader,
		NotificationsList
	},
	props: {
		open: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			fetchInterval: null
		}
	},
	computed: {
		activeTab() {
			return this.$store.state.notification.activeTab
		},
		notifications() {
			return this.$store.getters['notification/currentTabItems']
		},
		currentTabNextCursor() {
			return this.$store.getters['notification/currentTabNextCursor']
		},
		unreadCount() {
			return this.$store.state.notification.unreadCount
		},
		isLoadingList() {
			return this.$store.state.notification.loading.list
		},
		hasMoreNotifications() {
			return Boolean(this.currentTabNextCursor)
		},
		unreadTabLabel() {
			const label = this.$t('notification.unread-tab')

			return this.unreadCount > 0
				? `${label} (${this.unreadCount})`
				: label
		},
		emptyStateMessage() {
			return this.$store.getters['user/userIsApplicant']
				? this.$t('notification.empty-state.applicant')
				: this.$t('notification.empty-state.organization-member')
		},
		showMarkAllAsRead() {
			return this.activeTab === 'unread' && this.notifications.length > 0
		},
		showDeleteAll() {
			return this.activeTab === 'read' && this.notifications.length > 0
		},
		showHeaderActions() {
			return this.showMarkAllAsRead || this.showDeleteAll
		}
	},
	watch: {
		open(value) {
			if (value) {
				this.stopPolling()
				this.refreshNotifications()
				return
			}

			if (this.$store.getters['user/loggedIn']) {
				this.startPolling()
				return
			}

			this.stopPolling()
		}
	},
	methods: {
		handleRefreshError(error) {
			if (error.response && error.response.status === 401) {
				this.$store.commit('user/removeToken')
				this.stopPolling()
			}
		},
		async refreshNotificationCount() {
			try {
				await this.$store.dispatch('notification/fetchCount')
			} catch (error) {
				this.handleRefreshError(error)
			}
		},
		async refreshNotifications() {
			try {
				await this.refreshNotificationCount()

				if (this.open) {
					await this.$store.dispatch('notification/fetch', {
						state: this.activeTab
					})
				}
			} catch (error) {
				this.handleRefreshError(error)
			}
		},
		startPolling() {
			this.stopPolling()

			this.fetchInterval = setInterval(() => {
				if (!this.$store.getters['user/loggedIn']) {
					this.stopPolling()
					return
				}

				this.refreshNotificationCount()
			}, 30000)
		},
		stopPolling() {
			if (this.fetchInterval) {
				clearInterval(this.fetchInterval)
				this.fetchInterval = null
			}
		},
		switchTab(tab) {
			if (tab === this.activeTab) {
				return
			}

			this.$store.dispatch('notification/setActiveTab', tab)
			this.$store.dispatch('notification/fetch', {
				state: tab
			})
		},
		loadMoreNotifications() {
			if (!this.currentTabNextCursor || this.isLoadingList) {
				return Promise.resolve()
			}

			return this.$store.dispatch('notification/fetch', {
				state: this.activeTab,
				cursor: this.currentTabNextCursor,
				append: true
			})
		},
		async handleNotificationClick(notification) {
			if (this.isUnread(notification)) {
				await this.markNotificationAsRead(notification, {
					showToast: false
				})
			}

			if (notification.data && notification.data.url) {
				const navigation = this.$router?.push?.(
					this.buildNotificationRoute(notification)
				)

				if (navigation && typeof navigation.catch === 'function') {
					navigation.catch(() => {})
				}

				this.$emit('close')
			}
		},
		showSuccessToast(translationKey) {
			this.$store.dispatch('toast/push', {
				message: this.$t(translationKey),
				type: 'success',
				theme: 'light'
			})
		},
		async markNotificationAsRead(notification, { showToast = true } = {}) {
			try {
				await this.$store.dispatch(
					'notification/markRead',
					notification
				)

				if (showToast) {
					this.showSuccessToast(
						'notification.toast.marked-as-read'
					)
				}
			} catch (error) {}
		},
		async deleteNotification(notification) {
			try {
				await this.$store.dispatch(
					'notification/dismiss',
					notification
				)

				this.showSuccessToast('notification.toast.deleted')
			} catch (error) {}
		},
		async markAllAsRead() {
			try {
				await this.$store.dispatch('notification/readAll')

				this.showSuccessToast(
					'notification.toast.all-marked-as-read'
				)
			} catch (error) {}
		},
		async deleteAll() {
			try {
				await this.$store.dispatch('notification/dismissAll', {
					state: this.activeTab
				})

				this.showSuccessToast('notification.toast.all-deleted')
			} catch (error) {}
		},
		buildNotificationRoute(notification) {
			const url = notification?.data?.url || '/message'

			const separator = url.includes('?') ? '&' : '?'

			return `${url}${separator}notification=${notification.id}`
		},
		isUnread(notification) {
			return !notification.read_at
		},
		resolveContent(notification) {
			return resolveNotificationContent(notification, key => this.$t(key))
		},
		notificationRelativeTime(notification) {
			return this.$formatDateDistance(notification.created_at)
		},
		getAppearance(notification) {
			return getNotificationAppearance(notification, {
				isApplicant: this.$store.getters['user/userIsApplicant']
			})
		},
		getMedia(notification) {
			return getNotificationMedia(notification)
		},
		notificationImageUrl(notification) {
			return this.getMedia(notification).imageUrl
		},
		notificationFallbackIcon(notification) {
			const fallbackIcons = {
				applicant: 'IconUserProfile',
				organization: 'IconBuilding'
			}

			return (
				fallbackIcons[this.getMedia(notification).fallbackIcon] ||
				'IconUserProfile'
			)
		},
		notificationBadgeIcon(notification) {
			return this.getAppearance(notification).iconComponent
		},
		notificationBadgeClass(notification) {
			return this.getAppearance(notification).badgeClass
		}
	},
	mounted() {
		if (!this.$store.getters['user/loggedIn']) {
			return
		}

		if (this.open) {
			this.refreshNotifications()
		} else {
			this.refreshNotificationCount()
		}

		if (!this.open) {
			this.startPolling()
		}
	},
	destroyed() {
		this.stopPolling()
	}
}
</script>
